// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Who is in the arcade, and which machine they are standing at (M8-01).
//
// The roster, with **no Supabase in it and no browser global in it**, for two reasons that are
// both load-bearing.
//
// **The first is FR-030.** The VR arcade is on the front door: a visitor reaches it without an
// account, and `scripts/check-anonymous.mjs` walks the entry point's import closure and fails the
// build if anything in it can perform network I/O. A roster that imported `./supabase` would drag
// the SDK onto the anonymous path and break a promise that is not this feature's to break. So the
// arithmetic lives here and the connection lives in `src/client/account/venueLink.ts`, behind the
// lazy boundary — the same split the account surface already uses.
//
// **The second is that this file exists twice.** `App/Xr/VenuePresence.cs` is the same rules for
// the native client, held identical by `contracts/pinball/v1/fixtures/presence/bay-resolution.json`.
// Two clients that resolve a contested bay differently show two different arcades: I see you at
// bay 3 and you see yourself at bay 4, and my voice comes from the wrong place. That is precisely
// the failure the venue frame's fixtures exist to prevent (M8-02), one layer up.

import { PLAYFIELD_FRAME } from './venueFrame'

/**
 * How the player is here.
 *
 * 10 §5's three, spelled exactly as the contract spells them. `web-windowed` and `webxr` are the
 * *same browser* in two modes — a player who enters and leaves immersive mode changes kind
 * mid-session, which is why M8-06 debounces the downgrade rather than acting on it at once.
 */
export type ClientKind = 'web-windowed' | 'webxr' | 'quest-native'

export const CLIENT_KINDS: readonly ClientKind[] = ['web-windowed', 'webxr', 'quest-native']

export function isClientKind(value: unknown): value is ClientKind {
  return typeof value === 'string' && (CLIENT_KINDS as readonly string[]).includes(value)
}

/**
 * Which kind this client is, from facts the caller already has.
 *
 * Takes the facts rather than reading `navigator.xr`, so it is decidable in a test and so the one
 * place that knows whether an immersive session is running stays the one place that knows. A
 * native build passes `native: true` and the browser question never arises.
 */
export function clientKindFor(facts: {
  readonly native?: boolean
  readonly immersive?: boolean
}): ClientKind {
  if (facts.native === true) return 'quest-native'
  return facts.immersive === true ? 'webxr' : 'web-windowed'
}

/**
 * The venue's channel is **not here any more**, and the pointer is left because a reader looking
 * for it is asking the question this note answers.
 *
 * 10 §5 named it `arcade:venue`; M8-01 namespaced it to `pinball:arcade:venue` because
 * `realtime.messages` is shared by all twelve applications in this Supabase project exactly as the
 * schemas are, and two other products already have policies on it. M11-37 then made it one topic
 * *per room* — `pinball:arcade:venue:<room>` — because a single global topic delivers every pose to
 * every Pro player alive and nothing could refuse a seventh subscriber.
 *
 * So a client no longer knows its topic before it asks. It claims a seat, and the claim says which
 * room it is in. See `./venueRoom.ts` and `sql-scripts/0030-…`. It is deliberately **not**
 * re-exported from here: one name and one import path, so a reader who finds `venueTopic`
 * finds the file that explains it.
 */

/**
 * How many bays the venue has.
 *
 * Six, from 06 §4. Stated here rather than imported from `src/xr/arcadeBays.ts` because this
 * module may not depend on the renderer's half of the product — but a test asserts the two
 * numbers are equal, so they cannot drift apart in silence.
 */
export const VENUE_BAYS = 6

/**
 * What a client announces about itself on `arcade:venue`.
 *
 * 10 §5's four fields, plus the frame its poses travel in. In particular **there is no
 * timestamp**, and its absence is a decision — see `resolveBays`.
 */
export interface VenuePresence {
  /** The profile id. Presence is authenticated, so this is who the player is. */
  readonly participantId: string
  /** The display handle, for a nametag. */
  readonly handle: string
  /**
   * Which bay they claim. `null` asks to be placed — a client that has just arrived and has no
   * preference says so rather than guessing, and `resolveBays` deals it a free one.
   */
  readonly bay: number | null
  readonly clientKind: ClientKind
  /** The machine they are playing, or `null` while they are choosing one. */
  readonly machineId: string | null
  /**
   * The frame this client's poses are expressed in.
   *
   * **Announced once here rather than stamped on every pose**, because it is a property of the
   * client and not of each packet — and because `pose-v1` goes out fifteen times a second, where a
   * 34-byte frame name would roughly double the payload for a value that never changes.
   *
   * The point of a *versioned* frame (10 §5) is that a client which does not recognise one can say
   * so instead of guessing. Guessing is not a small error here: a future frame that differed only
   * in handedness would draw every remote player mirrored through the cabinet, and nothing would
   * fail. Announcing it in presence also means that is knowable **before the first pose arrives**
   * rather than fifteen times a second afterwards.
   */
  readonly poseFrame: string
}

/** The wire shape. Named separately because the wire is a contract and the interface is not. */
export function encodePresence(who: VenuePresence): Record<string, unknown> {
  return {
    participant_id: who.participantId,
    handle: who.handle,
    bay: who.bay,
    client_kind: who.clientKind,
    machine_id: who.machineId,
    pose_frame: who.poseFrame,
  }
}

/**
 * Read one presence row, or `undefined` if it is not one.
 *
 * **Unknown fields are ignored rather than refused**, which is the same additive-within-major
 * policy the HTTP contract is held to (03 §5) and matters more here than there: presence rows are
 * written by *other clients*, which may be a version ahead of this one. A reader that refused a
 * row carrying a field it had not heard of would make every additive change to the payload a
 * breaking one, and the symptom would be a player who is simply invisible.
 *
 * A row missing something required is dropped rather than defaulted. An unknown `client_kind`
 * is also dropped: guessing would draw a Quest player's head-and-hands avatar for a web player,
 * and an absent avatar is a better wrong answer than a misleading one.
 */
export function decodePresence(raw: unknown): VenuePresence | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const row = raw as Record<string, unknown>
  const participantId = row['participant_id']
  const handle = row['handle']
  const kind = row['client_kind']
  if (typeof participantId !== 'string' || participantId === '') return undefined
  if (typeof handle !== 'string') return undefined
  if (!isClientKind(kind)) return undefined
  const bay = row['bay']
  const machineId = row['machine_id']
  const poseFrame = row['pose_frame']
  return {
    participantId,
    handle,
    bay: typeof bay === 'number' && Number.isInteger(bay) && bay >= 0 ? bay : null,
    clientKind: kind,
    machineId: typeof machineId === 'string' && machineId !== '' ? machineId : null,
    // A row from a build that predates the field is read as the frame that build could only have
    // meant. Defaulting is right here and wrong for `client_kind`, and the difference is which
    // way the mistake fails: there has only ever been one pose frame, so assuming it is the truth,
    // where assuming a client kind would draw the wrong avatar for somebody.
    poseFrame: typeof poseFrame === 'string' && poseFrame !== '' ? poseFrame : PLAYFIELD_FRAME,
  }
}

/**
 * Can this client's poses be drawn at all?
 *
 * A player whose frame this build does not know is still **present** — they hold a bay, they have
 * a nametag, and their voice comes from the right place. What cannot be done is place their head,
 * because placing it would mean guessing at a convention. So they are in the room without an
 * avatar, which is a visible, explainable state, rather than an avatar in the wrong place, which
 * is not.
 */
export function posesAreDrawable(who: VenuePresence): boolean {
  return who.poseFrame === PLAYFIELD_FRAME
}

/**
 * Where everybody actually stands, given what everybody claims.
 *
 * Presence has no arbiter. Supabase hands every client the same set of rows and no opinion about
 * them, so if two players claim bay 3 the resolution has to be a **pure function of the rows that
 * every client computes independently and agrees on**. Anything else — asking the server, letting
 * the first claimant keep it, waiting a turn — either needs an authority this lane does not have
 * or converges to different answers on different screens.
 *
 * **The tiebreak is the participant id, and deliberately not "who got there first".** First-come
 * would need a timestamp in the payload, and that timestamp would be written by the client it
 * favours: a player who reported `joinedAt: 0` would win every contest for ever. The id is issued
 * by the server, is already in the row, and is exactly as arbitrary. What a player loses by losing
 * is one step sideways to the next free bay, so the rule does not have to be *fair* — it has to be
 * the same on every screen.
 *
 * A player with no free bay left resolves to `null`: **present in the venue, standing at no
 * machine.** Six bays is six bays, and a seventh player has to be somewhere. `null` is a state the
 * caller can render honestly — "the arcade is full" — where quietly doubling them up on bay 5
 * would put two avatars inside each other and two voices at one point in space.
 */
export function resolveBays(
  rows: readonly VenuePresence[],
  bayCount = VENUE_BAYS,
): ReadonlyMap<string, number | null> {
  const ordered = [...rows].sort(byParticipantId)
  const holder = new Map<number, string>()
  const settled = new Map<string, number | null>()
  const displaced: VenuePresence[] = []

  // First pass: an uncontested claim is honoured, and a contest goes to the lower id.
  for (const row of ordered) {
    if (row.bay === null || row.bay < 0 || row.bay >= bayCount) {
      displaced.push(row)
      continue
    }
    const already = holder.get(row.bay)
    if (already === undefined) {
      holder.set(row.bay, row.participantId)
      settled.set(row.participantId, row.bay)
    } else {
      // `ordered` is ascending, so the sitting tenant always has the lower id and always keeps it.
      displaced.push(row)
    }
  }

  // Second pass: everyone left over takes the lowest free bay, in the same order, so two clients
  // running this on the same rows deal them out the same way.
  for (const row of displaced) {
    const free = lowestFree(holder, bayCount)
    if (free === undefined) {
      settled.set(row.participantId, null)
      continue
    }
    holder.set(free, row.participantId)
    settled.set(row.participantId, free)
  }

  return settled
}

function byParticipantId(a: VenuePresence, b: VenuePresence): number {
  // Explicit comparison rather than the default, which stringifies and compares UTF-16 code units
  // by locale-independent rules that happen to match — but only by accident, and `sort` without a
  // comparator on objects would compare "[object Object]" to itself and change nothing.
  return a.participantId < b.participantId ? -1 : a.participantId > b.participantId ? 1 : 0
}

function lowestFree(holder: ReadonlyMap<number, string>, bayCount: number): number | undefined {
  for (let bay = 0; bay < bayCount; bay++) {
    if (!holder.has(bay)) return bay
  }
  return undefined
}

/**
 * Every presence row in a full state, whatever shape the transport hands it over in.
 *
 * Supabase's `presenceState()` is a map of key to a list of payloads, because one key can have
 * several sockets — a reconnect overlapping its predecessor, or the same player on a phone and a
 * headset. Flattening is the transport's only real decision and it is here rather than there so
 * it can be tested without a socket. Rows that do not decode are dropped, not counted: a
 * malformed row is one this client cannot draw, and pretending otherwise puts a nameless avatar
 * in the room.
 */
export function readPresenceState(state: unknown): readonly VenuePresence[] {
  if (typeof state !== 'object' || state === null) return []
  const rows: VenuePresence[] = []
  for (const entries of Object.values(state as Record<string, unknown>)) {
    if (!Array.isArray(entries)) continue
    for (const entry of entries) {
      const row = decodePresence(entry)
      if (row !== undefined) rows.push(row)
    }
  }
  return rows
}

/**
 * What this client should now say about itself, or `undefined` if what it already says is right.
 *
 * **Announce where you ended up, not where you asked to be.** Every client computed the same
 * resolution, so re-announcing changes nobody's answer — it makes my row agree with it. Without
 * that, a player who joins later reads my stale claim on bay 3, is displaced by a bay I do not
 * occupy, and stands one to the right of where the room has a gap.
 *
 * **It settles in one round**, which is the property that stops this being a feedback loop: the
 * bay I was resolved to is one nobody else holds, so resolving again returns it unchanged and
 * this function answers `undefined`. A client that re-announced unconditionally would trade
 * presence updates with every other client for as long as they were all in the room.
 */
export function reannouncement(
  mine: VenuePresence,
  settled: number | null | undefined,
): VenuePresence | undefined {
  if (settled === undefined) return undefined
  if (settled === mine.bay) return undefined
  return { ...mine, bay: settled }
}

/**
 * The venue as this client currently understands it.
 *
 * Holds the last full state and answers the questions a renderer asks. It is a class rather than
 * a bag of functions because presence arrives as a stream of events and the *last complete state*
 * is the thing worth keeping — Supabase sends a full sync on join and on every change, so there is
 * no incremental state to accumulate and no chance to drift out of step with the server.
 */
export class VenueRoster {
  readonly bayCount: number
  private rows: readonly VenuePresence[] = []
  private placement: ReadonlyMap<string, number | null> = new Map()

  constructor(bayCount = VENUE_BAYS) {
    this.bayCount = bayCount
  }

  /**
   * Replace everything with a full state.
   *
   * Rows that do not decode are already gone by the time they arrive here; duplicates by
   * participant id are collapsed, keeping the first, because one player is one player however
   * many sockets they have open — a reconnect that overlaps its own predecessor is normal and
   * must not appear as twins.
   */
  sync(rows: readonly VenuePresence[]): void {
    const unique: VenuePresence[] = []
    const seen = new Set<string>()
    for (const row of rows) {
      if (seen.has(row.participantId)) continue
      seen.add(row.participantId)
      unique.push(row)
    }
    this.rows = unique
    this.placement = resolveBays(unique, this.bayCount)
  }

  /** Everyone present, in participant-id order so two clients list them the same way. */
  get participants(): readonly VenuePresence[] {
    return [...this.rows].sort(byParticipantId)
  }

  get count(): number {
    return this.rows.length
  }

  /** Where this participant actually stands, after resolution. `undefined` if they are not here. */
  bayOf(participantId: string): number | null | undefined {
    return this.placement.get(participantId)
  }

  /** Who is at a bay, or `undefined` if it is empty. */
  at(bay: number): VenuePresence | undefined {
    for (const row of this.rows) {
      if (this.placement.get(row.participantId) === bay) return row
    }
    return undefined
  }

  /** Everyone but me — the ones this client draws avatars for. */
  others(selfId: string): readonly VenuePresence[] {
    return this.participants.filter((row) => row.participantId !== selfId)
  }

  /** Bays nobody stands at, in order. What the venue renders in attract mode (M5-21). */
  emptyBays(): readonly number[] {
    const free: number[] = []
    for (let bay = 0; bay < this.bayCount; bay++) {
      if (this.at(bay) === undefined) free.push(bay)
    }
    return free
  }

  /** No room left. The caller says so rather than standing somebody on somebody else. */
  get full(): boolean {
    return this.emptyBays().length === 0
  }
}
